/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { LoadingButton } from "@mui/lab"
import { useSnackbar } from "notistack"
import { isMedaicaApiError, MedaicaApiErrorType } from "@medaica/common/services/medaica-api-service"
import { logError } from "@medaica/common/services/util"
import { useBookingStores } from "../booking-context"
import BookingLayout from "../components/booking-layout"
import CodeInput from "../components/code-input"
import bookingCopy from "../bookingCopy"
import { bookingColors } from "../booking-theme"

const CODE_LENGTH = 6
const RESEND_COOLDOWN_SECONDS = 60
// After this many failed attempts, surface calmer guidance + a support link. Client-side only:
// a rate-limited/decoy session can't be flagged by the API (anti-enumeration), so this helps
// any stuck user — throttled-decoy or just mistyping — without revealing which they are.
const HELP_AFTER_ATTEMPTS = 3
// After this many resend requests, surface the "there may be a wait" guidance — the resend
// response is neutral, so this is how a rate-limited user learns why no new code arrives.
const HELP_AFTER_RESENDS = 2

const mapVerifyError = (error: unknown): string => {
  const copy = bookingCopy.verify.errors
  if (isMedaicaApiError(error)) {
    const apiError = error as { type: MedaicaApiErrorType; attemptsRemaining?: number }
    switch (apiError.type) {
      case MedaicaApiErrorType.CODE_LOCKED:
        return copy.locked
      case MedaicaApiErrorType.CODE_EXPIRED:
        return copy.expired
      case MedaicaApiErrorType.CODE_SEND_FAILED:
        return copy.sendFailed
      case MedaicaApiErrorType.INVALID_DATA:
        // attemptsRemaining rides on both mock errors and (via the interceptor
        // passthrough) real API errors; show the count only when positive — never "0 left".
        return typeof apiError.attemptsRemaining === "number" && apiError.attemptsRemaining > 0
          ? copy.wrongCode(apiError.attemptsRemaining)
          : copy.wrongCodeGeneric
      default:
        return copy.generic
    }
  }
  return copy.generic
}

const Verify = observer((): ReactElement => {
  const { sessionStore } = useBookingStores()
  const { enqueueSnackbar } = useSnackbar()
  const [code, setCode] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [resendCount, setResendCount] = useState(0)

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined
    }
    const timer = setTimeout(() => setResendCooldown((seconds) => seconds - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  const submit = async (value: string): Promise<void> => {
    if (value.length < CODE_LENGTH || submitting) {
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await sessionStore.submitCode(value)
      // navigation is handled by the store (→ account, or the resumed step)
    } catch (submitError) {
      setError(mapVerifyError(submitError))
      setCode("")
      setAttempt((current) => current + 1)
    } finally {
      setSubmitting(false)
    }
  }

  const resend = async (): Promise<void> => {
    if (resendCooldown > 0) {
      return
    }
    setError(null)
    // Start the cooldown on click (not after the request resolves) so the button disables
    // immediately — otherwise rapid clicks fire several resendCode() calls. Reset on failure.
    setResendCooldown(RESEND_COOLDOWN_SECONDS)
    setResendCount((count) => count + 1)
    try {
      await sessionStore.resendCode()
      enqueueSnackbar(bookingCopy.verify.resent, { variant: "success" })
    } catch (resendError) {
      logError(resendError)
      setError(bookingCopy.verify.errors.sendFailed)
      setResendCooldown(0)
    }
  }

  // The locked message already directs to support, so the inline "Request a new code" is hidden
  // for it (the footer link still offers a retry). Compares to the same copy the error was set from.
  const codeLocked = error === bookingCopy.verify.errors.locked

  return (
    <BookingLayout>
      <div className="max-w-md mx-auto mt-16 bg-white rounded-lg shadow-sm px-8 py-10">
        <h1 className="text-2xl font-bold text-center" style={{ color: bookingColors.primary }}>
          {bookingCopy.verify.title}
        </h1>
        <p className="mt-2 mb-8 text-center text-gray-600">
          {sessionStore.entryMode === "email"
            ? bookingCopy.verify.neutralSubtitle
            : bookingCopy.verify.subtitle(sessionStore.maskedEmail ?? "your email")}
        </p>

        <CodeInput
          key={attempt}
          length={CODE_LENGTH}
          onChange={setCode}
          onComplete={(value) => void submit(value)}
          disabled={submitting}
        />

        {error ? (
          <div className="mt-3 text-center">
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
            {/* Same action as the footer button; resend() clears `error`, so this link and the
                message above it disappear together (cooldown-gated, with the same (Xs) countdown as
                the footer). Hidden when locked — that message directs to support instead. */}
            {codeLocked ? null : (
              <p className="mt-1 text-sm text-gray-500">
                <button
                  type="button"
                  className="font-medium text-red-600 underline disabled:text-gray-400 disabled:no-underline"
                  disabled={resendCooldown > 0}
                  onClick={() => void resend()}
                >
                  {bookingCopy.verify.resend}
                </button>
                {resendCooldown > 0 ? ` (${resendCooldown}s)` : ""}
              </p>
            )}
            {attempt >= HELP_AFTER_ATTEMPTS ? (
              <p className="mt-3 text-xs text-gray-500">
                {bookingCopy.verify.stuckHelp}{" "}
                <a href={`mailto:${bookingCopy.supportEmail}`} className="underline">
                  {bookingCopy.supportEmail}
                </a>
              </p>
            ) : null}
          </div>
        ) : null}

        <LoadingButton
          fullWidth
          variant="contained"
          size="large"
          sx={{ mt: 3 }}
          loading={submitting}
          disabled={code.length < CODE_LENGTH}
          onClick={() => void submit(code)}
        >
          {bookingCopy.verify.continueButton}
        </LoadingButton>

        <p className="mt-5 text-center text-sm text-gray-500">
          {bookingCopy.verify.didntGetIt}{" "}
          <button
            type="button"
            className="font-medium text-primary disabled:text-gray-400"
            disabled={resendCooldown > 0}
            onClick={() => void resend()}
          >
            {bookingCopy.verify.resend}
          </button>
          {resendCooldown > 0 ? ` (${resendCooldown}s)` : ""} · {bookingCopy.verify.codeExpires}
        </p>
        {resendCount >= HELP_AFTER_RESENDS ? (
          <p className="mt-2 text-center text-xs text-gray-500">
            {bookingCopy.verify.resendLimitHelp}{" "}
            <a href={`mailto:${bookingCopy.supportEmail}`} className="underline">
              {bookingCopy.supportEmail}
            </a>
          </p>
        ) : null}
        <p className="mt-2 text-center text-sm text-gray-500">{bookingCopy.verify.spamHint}</p>
      </div>
      <p className="mt-6 text-center text-sm text-gray-500">{bookingCopy.verify.personalNote}</p>
    </BookingLayout>
  )
})

export default Verify
